import React, { useEffect } from 'react';
import { X, Mail, Lock, User } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUiStore } from '../../store/uiStore';
import { useAuth } from '../../hooks/useAuth';

// ── Schemas ───────────────────────────────────────────────────────────────────

const loginSchema = z.object({
  email:    z.string().email('Invalid email'),
  password: z.string().min(1, 'Password required'),
});

const registerSchema = loginSchema.extend({
  firstName: z.string().min(1, 'First name required'),
  lastName:  z.string().optional(),
  confirm:   z.string(),
}).refine((d) => d.password === d.confirm, { message: 'Passwords do not match', path: ['confirm'] });

type LoginForm    = z.infer<typeof loginSchema>;
type RegisterForm = z.infer<typeof registerSchema>;

// ── Component ─────────────────────────────────────────────────────────────────

// Email accounts need the /api/auth backend, which static hosting doesn't have; flip on once one is deployed.
const EMAIL_AUTH_ENABLED = false;

const AuthModal: React.FC = () => {
  const { authModalOpen, authTab, closeAuthModal, openAuthModal } = useUiStore();
  const { login, register, loginWithAmazon, isLoggingIn, isRegistering } = useAuth();

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const registerForm = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: '', password: '', confirm: '', firstName: '', lastName: '' },
  });

  // Reset forms on tab switch
  useEffect(() => { loginForm.reset(); registerForm.reset(); }, [authTab]);

  // Close on Escape
  useEffect(() => {
    if (!authModalOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAuthModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [authModalOpen, closeAuthModal]);

  if (!authModalOpen) return null;

  const showEmailForms = EMAIL_AUTH_ENABLED;
  const isLogin = !showEmailForms || authTab === 'login';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={closeAuthModal}
      role="dialog"
      aria-modal="true"
      aria-label={isLogin ? 'Sign in' : 'Create account'}
    >
      <div
        className="card w-full max-w-md p-8 animate-slide-down"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {showEmailForms ? (isLogin ? 'Welcome back' : 'Create account') : 'Sign in'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {showEmailForms
                ? (isLogin ? 'Sign in to your account' : 'Join Amazon Onlines today')
                : 'Use your Amazon account — no new password needed'}
            </p>
          </div>
          <button onClick={closeAuthModal} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        {showEmailForms && (
        <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700/50 p-1 mb-6">
          {(['login', 'register'] as const).map((t) => (
            <button
              key={t}
              onClick={() => openAuthModal(t)}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-all ${
                authTab === t
                  ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {t === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>
        )}

        {/* Login form */}
        {showEmailForms && authTab === 'login' && (
          <form
            onSubmit={loginForm.handleSubmit((d) => login({ email: d.email, password: d.password }))}
            className="space-y-4"
          >
            <Field label="Email" error={loginForm.formState.errors.email?.message}>
              <InputIcon icon={<Mail />}>
                <input type="email" placeholder="you@example.com" className="input pl-10" {...loginForm.register('email')} />
              </InputIcon>
            </Field>
            <Field label="Password" error={loginForm.formState.errors.password?.message}>
              <InputIcon icon={<Lock />}>
                <input type="password" placeholder="••••••••" className="input pl-10" {...loginForm.register('password')} />
              </InputIcon>
            </Field>
            <button type="submit" disabled={isLoggingIn} className="btn-primary w-full mt-2">
              {isLoggingIn ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}

        {/* Register form */}
        {showEmailForms && authTab === 'register' && (
          <form
            onSubmit={registerForm.handleSubmit((d) =>
              register({ email: d.email, password: d.password, firstName: d.firstName, lastName: d.lastName }),
            )}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" error={registerForm.formState.errors.firstName?.message}>
                <InputIcon icon={<User />}>
                  <input type="text" placeholder="Jane" className="input pl-10" {...registerForm.register('firstName')} />
                </InputIcon>
              </Field>
              <Field label="Last name">
                <InputIcon icon={<User />}>
                  <input type="text" placeholder="Doe" className="input pl-10" {...registerForm.register('lastName')} />
                </InputIcon>
              </Field>
            </div>
            <Field label="Email" error={registerForm.formState.errors.email?.message}>
              <InputIcon icon={<Mail />}>
                <input type="email" placeholder="you@example.com" className="input pl-10" {...registerForm.register('email')} />
              </InputIcon>
            </Field>
            <Field label="Password" error={registerForm.formState.errors.password?.message}>
              <InputIcon icon={<Lock />}>
                <input type="password" placeholder="At least 8 characters" className="input pl-10" {...registerForm.register('password')} />
              </InputIcon>
            </Field>
            <Field label="Confirm password" error={registerForm.formState.errors.confirm?.message}>
              <InputIcon icon={<Lock />}>
                <input type="password" placeholder="Repeat password" className="input pl-10" {...registerForm.register('confirm')} />
              </InputIcon>
            </Field>
            <button type="submit" disabled={isRegistering} className="btn-primary w-full mt-2">
              {isRegistering ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
        )}

        {/* ── Social login ─────────────────────────────────────────────────── */}
        {showEmailForms && (
        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-gray-700" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white dark:bg-gray-800 px-3 text-xs text-gray-400 uppercase tracking-wide">
              or continue with
            </span>
          </div>
        </div>
        )}

        <button
          type="button"
          onClick={loginWithAmazon}
          className="w-full flex items-center justify-center gap-3 bg-[#FF9900] hover:bg-[#e68900] active:bg-[#d07d00] text-black font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
          aria-label="Sign in with Amazon"
        >
          {/* Amazon "a" logomark */}
          <svg viewBox="0 0 38 38" className="w-5 h-5 shrink-0" aria-hidden="true">
            <path
              fill="#000"
              d="M23.3 22.6c-3.4 2.5-8.3 3.8-12.5 3.8-5.9 0-11.2-2.2-15.2-5.8-.3-.3 0-.7.3-.5 4.3 2.5 9.7 4 15.2 4 3.7 0 7.8-.8 11.6-2.4.5-.3 1 .3.6.9z"
            />
            <path
              fill="#000"
              d="M24.8 20.9c-.4-.6-2.9-.3-4-.1-.3 0-.4-.2-.1-.4 2-1.4 5.3-1 5.7-.5.4.5-.1 3.8-2 5.4-.3.2-.6.1-.5-.2.4-1.1 1.3-3.6.9-4.2z"
            />
            <path
              fill="#000"
              fillRule="evenodd"
              d="M21.3 3.5C18.8 1.2 15.2 0 12 0 7.2 0 2.9 2 .1 5.3c-.2.3.1.6.4.4C3 3.7 7 2.4 11.2 2.4c3.4 0 7.1 1 9.8 3.2.3.2.7-.1.5-.4l-.2-.2v-.5zM6.3 8.5C4.1 10.5 3 13.3 3 16.2c0 2 .5 3.9 1.4 5.6.1.2.4.1.4-.1-.2-1.6-.1-3.2.2-4.8.7-3.5 2.7-5.7 5.5-7 .3-.1.2-.5-.1-.5-1.6.1-3.1.5-4.1 1.1z"
            />
          </svg>
          Sign in with Amazon
        </button>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-5">
          By continuing you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

// ── Helper components ─────────────────────────────────────────────────────────

const Field: React.FC<React.PropsWithChildren<{ label: string; error?: string }>> = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const InputIcon: React.FC<React.PropsWithChildren<{ icon: React.ReactNode }>> = ({ icon, children }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4">{icon}</span>
    {children}
  </div>
);

export default AuthModal;
